import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Search, Map, List, LayoutPanelLeft, Compass, CalendarPlus } from 'lucide-react';
import EventMap from './components/Map';
import EventList from './components/EventList';
import FilterBar, { applyFilters, getActiveFilterCount, DEFAULT_FILTERS } from './components/FilterBar';
import type { Filters } from './components/FilterBar';
import { useEvents } from './hooks/useEvents';
import { supabase, searchEvents } from './lib/supabase';
import { useDebounce } from './hooks/useDebounce';
import type { Event } from './types/event';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import { useAuth } from './hooks/useAuth';
import NowStrip from './components/NowStrip';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPage';
import GuidePage from './components/GuidePage';
import SubmitEventPage from './components/SubmitEventPage';
import CookieConsent, { ConsentProvider } from './components/CookieConsent';
import { navigate } from './lib/navigate';

type ViewMode = 'map' | 'list' | 'split';
type AppRoute = '' | 'admin' | 'about' | 'contact' | 'privacy' | 'guide' | 'submit';

const viewButtons: { mode: ViewMode; icon: typeof Map; label: string }[] = [
  { mode: 'map', icon: Map, label: 'Map' },
  { mode: 'split', icon: LayoutPanelLeft, label: 'Split' },
  { mode: 'list', icon: List, label: 'List' },
];

function getRoute(pathname: string): AppRoute {
  if (pathname === '/admin') return 'admin';
  if (pathname === '/about') return 'about';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/privacy') return 'privacy';
  if (pathname === '/guide') return 'guide';
  if (pathname === '/submit') return 'submit';
  return '';
}

function App() {
  const [route, setRoute] = useState<AppRoute>(() => getRoute(window.location.pathname));
  const [isAdminVerified, setIsAdminVerified] = useState(false);
  const { loading: authLoading, isAuthenticated, user } = useAuth();

  useEffect(() => {
    const onPop = () => setRoute(getRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Verify admin status when session is already present (e.g. page refresh)
  useEffect(() => {
    if (route !== 'admin' || authLoading || !isAuthenticated || isAdminVerified || !user?.email) return;
    supabase.from('admin_users').select('email').eq('email', user.email!).maybeSingle()
      .then(({ data }) => { if (data) setIsAdminVerified(true); });
  }, [route, authLoading, isAuthenticated, isAdminVerified, user?.email]);

  // Clear admin verification on sign-out
  useEffect(() => {
    if (!isAuthenticated) setIsAdminVerified(false);
  }, [isAuthenticated]);

  const goHome = () => navigate('/');

  // Non-map routes don't need the consent banner (no ads)
  if (route === 'about') return <AboutPage onBack={goHome} />;
  if (route === 'contact') return <ContactPage onBack={goHome} />;
  if (route === 'privacy') return <PrivacyPage onBack={goHome} />;
  if (route === 'guide') return (
    <ConsentProvider>
      <GuidePage onBack={goHome} />
      <CookieConsent />
    </ConsentProvider>
  );
  if (route === 'submit') return <SubmitEventPage onBack={goHome} />;

  if (route === 'admin') {
    if (authLoading) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-zinc-700 border-t-amber-500" />
        </div>
      );
    }
    if (!isAuthenticated || !isAdminVerified) {
      return <AdminLogin onLogin={() => setIsAdminVerified(true)} />;
    }
    return (
      <AdminDashboard
        userEmail={user?.email ?? ''}
        onLogout={() => {
          setIsAdminVerified(false);
          navigate('/');
        }}
      />
    );
  }

  return (
    <ConsentProvider>
      <EventMapApp />
      <CookieConsent />
    </ConsentProvider>
  );
}

function EventMapApp() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Event[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== 'undefined' && window.innerWidth >= 768 ? 'split' : 'map'
  );
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const PITTSBURGH_CENTER = { latitude: 40.4407, longitude: -80.0025 };

  const [viewState, setViewState] = useState({
    longitude: PITTSBURGH_CENTER.longitude,
    latitude: PITTSBURGH_CENTER.latitude,
    zoom: 11
  });

  const handleViewStateChange = useCallback((vs: typeof viewState) => {
    setViewState(vs);
  }, []);

  const [radius, setRadius] = useState(50000);

  const { events, isLoading, error } = useEvents({
    latitude: PITTSBURGH_CENTER.latitude,
    longitude: PITTSBURGH_CENTER.longitude,
    radius,
  });

  const handleSearch = useCallback(async (query: string) => {
    if (!query?.trim()) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const results = await searchEvents(query);

      if (results.length === 0) {
        setSearchError(`No results found for "${query}"`);
        setSearchResults([]);
      } else {
        setSearchResults(results);
        setSearchError(null);

        const firstWithCoords = results.find(r => r.latitude && r.longitude);
        if (firstWithCoords) {
          setViewState({
            latitude: firstWithCoords.latitude,
            longitude: firstWithCoords.longitude,
            zoom: results.length > 1 ? 8 : 11,
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to perform search. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  React.useEffect(() => {
    handleSearch(debouncedSearch);
  }, [debouncedSearch, handleSearch]);

  const handleCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setViewState({
            longitude: position.coords.longitude,
            latitude: position.coords.latitude,
            zoom: 13
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const handleEventClick = useCallback((event: Event) => {
    setSelectedEventId(event.id);
    if (event.latitude && event.longitude) {
      setViewState({ latitude: event.latitude, longitude: event.longitude, zoom: 14 });
    }
  }, []);

  const handleSelectEvent = useCallback((id: string | null) => {
    setSelectedEventId(id);
  }, []);

  const baseEvents = searchQuery.trim() ? searchResults : events;
  const displayedEvents = useMemo(() => applyFilters(baseEvents, filters), [baseEvents, filters]);
  const activeFilterCount = getActiveFilterCount(filters);

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col bg-slate-100">
      <h1 className="sr-only">Pitt Fest Pittsburgh &amp; Pitt Parties — Things to Do in Pittsburgh, PA</h1>
      {/* Header */}
      <header className="bg-zinc-950 text-white z-20 flex-shrink-0 border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3 sm:gap-5">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <span className="text-[17px] font-bold tracking-tight leading-none m-0">
                Get Out <span className="text-amber-400">Pitt</span>
              </span>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-xl">
              <div className="relative group">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${isSearching ? 'text-amber-400 animate-pulse' : 'text-zinc-400 group-focus-within:text-amber-400'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search events..."
                  className="w-full pl-9 pr-4 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50 focus:bg-zinc-900/80 transition-all"
                  aria-label="Search events"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-600 border-t-amber-400" />
                  </div>
                )}
              </div>
            </div>

            {/* View Toggle — desktop only (mobile uses bottom nav) */}
            <div className="hidden md:flex items-center bg-zinc-900 rounded-lg p-0.5 gap-0.5 flex-shrink-0">
              {viewButtons.map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  title={label}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${
                    viewMode === mode
                      ? 'bg-amber-500 text-zinc-950 shadow-lg shadow-amber-500/20'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden lg:inline">{label}</span>
                </button>
              ))}
            </div>

            {/* Location */}
            <button
              onClick={handleCurrentLocation}
              className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-700 px-3 py-2 rounded-lg transition-all text-sm flex-shrink-0"
              title="My Location"
            >
              <Compass className="w-4 h-4" />
              <span className="hidden lg:inline">My Location</span>
            </button>

            {/* Submit Event */}
            <button
              onClick={() => navigate('/submit')}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-3 py-2 rounded-lg transition-all text-sm flex-shrink-0"
              title="Submit an Event"
            >
              <CalendarPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Submit Event</span>
            </button>
          </div>
        </div>
      </header>

      {/* Search error toast */}
      {searchError && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg shadow-glass-lg text-sm font-medium">
            {searchError}
          </div>
        </div>
      )}

      {/* Happening now / starting soon strip */}
      <NowStrip events={displayedEvents} onEventClick={handleEventClick} />

      {/* Main content area */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {error && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-glass text-sm">
              {error}
            </div>
          </div>
        )}

        {/* List panel — full-width in list mode; desktop-only sidebar in split mode */}
        {(viewMode === 'list' || viewMode === 'split') && (
          <aside
            className={`bg-white flex flex-col flex-shrink-0 overflow-hidden border-r border-slate-200 ${
              viewMode === 'list'
                ? 'w-full'
                : 'hidden md:flex w-80 lg:w-[360px] xl:w-[400px]'
            }`}
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">
                  {isLoading ? 'Loading...' : `${displayedEvents.length} event${displayedEvents.length !== 1 ? 's' : ''}`}
                </span>
                {!isLoading && displayedEvents.length > 0 && (
                  <span className="text-[11px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {searchQuery.trim() ? 'Search' : 'Nearby'}
                  </span>
                )}
                {activeFilterCount > 0 && !isLoading && (
                  <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <FilterBar filters={filters} onChange={setFilters} allEvents={baseEvents} />
            <EventList
              events={displayedEvents}
              isLoading={isLoading}
              onEventClick={handleEventClick}
            />
          </aside>
        )}

        {/* Map */}
        {(viewMode === 'map' || viewMode === 'split') && (
          <main className="flex-1 relative min-w-0">
            {isLoading && viewMode === 'map' && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-amber-500 mb-3" />
                  <p className="text-slate-500 text-sm font-medium">Loading events...</p>
                </div>
              </div>
            )}
            <EventMap
              events={displayedEvents}
              viewState={viewState}
              onViewStateChange={handleViewStateChange}
              selectedEventId={selectedEventId}
              onSelectEvent={handleSelectEvent}
              fetchLat={PITTSBURGH_CENTER.latitude}
              fetchLng={PITTSBURGH_CENTER.longitude}
              radiusMeters={radius}
              onRadiusChange={setRadius}
            />
          </main>
        )}
      </div>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden flex-shrink-0 bg-zinc-950 border-t border-zinc-800 flex items-center z-20">
        {([
          { mode: 'map' as ViewMode, icon: Map, label: 'Map' },
          { mode: 'list' as ViewMode, icon: List, label: 'List' },
        ] as { mode: ViewMode; icon: typeof Map; label: string }[]).map(({ mode, icon: Icon, label }) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
              viewMode === mode
                ? 'text-amber-400'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Icon className="w-5 h-5" />
            {label}
          </button>
        ))}
        <button
          onClick={() => navigate('/submit')}
          className="flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium text-amber-400 hover:text-amber-300 transition-colors"
        >
          <CalendarPlus className="w-5 h-5" />
          Submit
        </button>
      </nav>

      {/* Footer — unobtrusive, desktop only */}
      <footer className="hidden md:flex flex-shrink-0 items-center justify-center gap-4 py-1.5 bg-zinc-950 border-t border-zinc-800/60 z-20">
        <a
          href="/guide"
          onClick={(e) => { e.preventDefault(); navigate('/guide'); }}
          className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Pittsburgh Guide
        </a>
        <span className="text-zinc-700 text-[10px]">·</span>
        <a
          href="/about"
          onClick={(e) => { e.preventDefault(); navigate('/about'); }}
          className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          About
        </a>
        <span className="text-zinc-700 text-[10px]">·</span>
        <a
          href="/contact"
          onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
          className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Contact
        </a>
        <span className="text-zinc-700 text-[10px]">·</span>
        <a
          href="/privacy"
          onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
          className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Privacy &amp; Cookies
        </a>
        <span className="text-zinc-700 text-[10px]">·</span>
        <span className="text-[11px] text-zinc-600">
          &copy; {new Date().getFullYear()} Get Out Pitt
        </span>
      </footer>
    </div>
  );
}

export default App;
